import { useEffect, useMemo, useRef, useState } from 'react';
import { Table as STable, Loader, Message, Pagination, Dropdown, Input, Grid, GridColumn, Icon } from 'semantic-ui-react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { getUserActivityLogs } from '~/api/admin';
import { UserActivityTypes } from '~/api/user';
import { UserRole } from '~/constants';
import { Button } from '~/components';
import '~/features/admin/companies/styles.scss';

type ActivityRow = {
  id: number;
  createdAt: string;
  activityType: string;
  metadata?: Record<string, unknown> | null;
  user?: {
    id: number;
    name: string;
    email: string;
    role?: { code: string };
    installer?: { company?: { name: string } };
    investor?: { company?: { name: string } };
    company?: { name: string };
  };
  project?: {
    id: number;
    parentId?: number | null;
    name: string;
    portfolio?: { id: number; parentId?: number | null; name: string };
  };
  portfolio?: { id: number; parentId?: number | null; name: string };
};

const PAGE_SIZE = 50;

const DEFAULT_ROLES = [UserRole.INVESTOR, UserRole.INSTALLER];
const colStyle = { padding: '1rem 0 0 1rem' };
const controlFontSize = { fontSize: '.9rem' };

function truncateForMeta(s: string, max = 56): string {
  if (s.length <= max) {
    return s;
  }
  return `${s.slice(0, max - 1)}...`;
}

function isDefaultRolesSelection(roles: string[]): boolean {
  return [...roles].sort().join(',') === [...DEFAULT_ROLES].sort().join(',');
}

function parseParamsFromSearch(search: string) {
  const params = new URLSearchParams(search);
  const userRolesParam = params.get('userRoles');
  const userRoles = userRolesParam
    ? userRolesParam.split(',').filter(Boolean)
    : DEFAULT_ROLES;
  return {
    userName: params.get('userName') || '',
    projectName: params.get('projectName') || '',
    portfolioName: params.get('portfolioName') || '',
    activityType: params.get('activityType') || undefined,
    userRoles: userRoles.length > 0 ? userRoles : DEFAULT_ROLES,
    page: Math.max(1, parseInt(params.get('page') || '1', 10)),
    sortDirection: (params.get('sortDirection') === 'asc' ? 'asc' : 'desc') as 'asc' | 'desc',
  };
}

function buildSearchFromState(state: {
  userFilter: string;
  projectFilter: string;
  portfolioFilter: string;
  typeFilter: string | undefined;
  userRolesFilter: string[];
  currentPage: number;
  sortDirection: 'asc' | 'desc';
}) {
  const p = new URLSearchParams();
  if (state.userFilter.trim()) p.set('userName', state.userFilter.trim());
  if (state.projectFilter.trim()) p.set('projectName', state.projectFilter.trim());
  if (state.portfolioFilter.trim()) p.set('portfolioName', state.portfolioFilter.trim());
  if (state.typeFilter) p.set('activityType', state.typeFilter);
  if (state.userRolesFilter.length > 0) {
    p.set('userRoles', state.userRolesFilter.join(','));
  }
  p.set('page', String(state.currentPage));
  p.set('sortDirection', state.sortDirection);
  return p.toString();
}

export default function UserActivityTable() {
  const location = useLocation();
  const history = useHistory();
  const [activities, setActivities] = useState<ActivityRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [userFilter, setUserFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [portfolioFilter, setPortfolioFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<string | undefined>(undefined);
  const [userRolesFilter, setUserRolesFilter] = useState<string[]>(DEFAULT_ROLES);

  const hasSyncedFromUrl = useRef(false);

  // Sync state from URL when it changes (e.g. browser back/forward or opening a shared link)
  useEffect(() => {
    const parsed = parseParamsFromSearch(location.search);
    setUserFilter(parsed.userName);
    setProjectFilter(parsed.projectName);
    setPortfolioFilter(parsed.portfolioName);
    setTypeFilter(parsed.activityType);
    setUserRolesFilter(parsed.userRoles);
    setCurrentPage(parsed.page);
    setSortDirection(parsed.sortDirection);
  }, [location.search]);

  // Persist filters/page/sort to URL so back button and bookmarks restore the same view.
  // Skip the first run so we don't overwrite URL params before sync-from-URL has run.
  useEffect(() => {
    if (!hasSyncedFromUrl.current) {
      hasSyncedFromUrl.current = true;
      return;
    }
    const search = buildSearchFromState({
      userFilter,
      projectFilter,
      portfolioFilter,
      typeFilter,
      userRolesFilter,
      currentPage,
      sortDirection,
    });
    const currentParsed = parseParamsFromSearch(location.search);
    const urlMatchesState =
      currentParsed.userName === userFilter &&
      currentParsed.projectName === projectFilter &&
      currentParsed.portfolioName === portfolioFilter &&
      currentParsed.activityType === (typeFilter || '') &&
      currentParsed.page === currentPage &&
      currentParsed.sortDirection === sortDirection &&
      currentParsed.userRoles.join(',') === userRolesFilter.join(',');
    if (!urlMatchesState) {
      history.replace(`${location.pathname}?${search}`);
    }
  }, [
    location.pathname,
    userFilter,
    projectFilter,
    portfolioFilter,
    typeFilter,
    userRolesFilter.join(','),
    currentPage,
    sortDirection,
  ]);

  useEffect(() => {
    document.title = 'User Activity | Conductor Solar';
  }, []);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        setError(null);

        const offset = (currentPage - 1) * PAGE_SIZE;
        const params: any = {
          limit: PAGE_SIZE,
          offset,
          sortDirection,
        };

        if (userFilter.trim()) params.userName = userFilter.trim();
        if (projectFilter.trim()) params.projectName = projectFilter.trim();
        if (portfolioFilter.trim()) params.portfolioName = portfolioFilter.trim();
        if (typeFilter) params.activityType = typeFilter;
        if (userRolesFilter.length > 0) {
          // Send as comma-separated list so backend can reliably parse all roles
          params.userRoles = userRolesFilter.join(',');
        }

        const response = await getUserActivityLogs(params);

        setActivities(response.activities || []);
        setTotal(response.total || 0);
      } catch (e: any) {
        setError(e?.message || 'Failed to fetch user activity');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [currentPage, sortDirection, userFilter, projectFilter, portfolioFilter, typeFilter, userRolesFilter]);

  const userRoleOptions = [
    { key: UserRole.INVESTOR, value: UserRole.INVESTOR, text: 'Investor' },
    { key: UserRole.INSTALLER, value: UserRole.INSTALLER, text: 'Installer' },
    { key: UserRole.ADMIN, value: UserRole.ADMIN, text: 'Admin' },
    { key: UserRole.EXTERNAL, value: UserRole.EXTERNAL, text: 'External' },
  ];

  const handleToggleSortByDate = () => {
    setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const typeOptions = Object.values(UserActivityTypes).map((code) => ({
    key: code,
    value: code,
    text: (code as string).replace(/_/g, ' '),
  }));

  const roleLabel: Record<string, string> = {
    [UserRole.INVESTOR]: 'Investor',
    [UserRole.INSTALLER]: 'Installer',
    [UserRole.ADMIN]: 'Admin',
    [UserRole.EXTERNAL]: 'External',
  };

  const getSortIcon = () => (sortDirection === 'asc' ? ' ▲' : ' ▼');

  const hasFilters = useMemo(() => {
    return (
      userFilter.trim().length > 0 ||
      projectFilter.trim().length > 0 ||
      portfolioFilter.trim().length > 0 ||
      Boolean(typeFilter) ||
      !isDefaultRolesSelection(userRolesFilter)
    );
  }, [userFilter, projectFilter, portfolioFilter, typeFilter, userRolesFilter]);

  const clearAllFilters = () => {
    setUserFilter('');
    setProjectFilter('');
    setPortfolioFilter('');
    setTypeFilter(undefined);
    setUserRolesFilter(DEFAULT_ROLES);
    setCurrentPage(1);
  };

  const activeFilterDescription = useMemo(() => {
    const parts: string[] = [];
    if (userFilter.trim()) {
      parts.push(`User contains "${truncateForMeta(userFilter.trim())}"`);
    }
    if (projectFilter.trim()) {
      parts.push(`Project contains "${truncateForMeta(projectFilter.trim())}"`);
    }
    if (portfolioFilter.trim()) {
      parts.push(`Portfolio contains "${truncateForMeta(portfolioFilter.trim())}"`);
    }
    if (typeFilter) {
      parts.push(`Type: ${typeFilter.replace(/_/g, ' ')}`);
    }
    if (!isDefaultRolesSelection(userRolesFilter)) {
      parts.push(`Roles: ${[...userRolesFilter].sort().join(', ')}`);
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [userFilter, projectFilter, portfolioFilter, typeFilter, userRolesFilter]);

  return (
    <div>
      <div className="card">
        <div style={{ margin: '1rem 0 2rem', paddingRight: '1rem' }}>
          <Grid stackable>
            <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
              <Input
                placeholder='User name contains...'
                value={userFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setUserFilter(e.target.value);
                }}
                fluid
                style={controlFontSize}
              />
            </GridColumn>

            <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
              <Input
                placeholder='Project name contains...'
                value={projectFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setProjectFilter(e.target.value);
                }}
                fluid
                style={controlFontSize}
              />
            </GridColumn>

            <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
              <Input
                placeholder='Portfolio name contains...'
                value={portfolioFilter}
                onChange={(e) => {
                  setCurrentPage(1);
                  setPortfolioFilter(e.target.value);
                }}
                fluid
                style={controlFontSize}
              />
            </GridColumn>

            <GridColumn mobile={16} tablet={6} computer={3} style={colStyle}>
              <Dropdown
                placeholder='All types...'
                fluid
                search
                selection
                clearable
                options={typeOptions}
                value={typeFilter}
                onChange={(_, { value }) => {
                  setCurrentPage(1);
                  setTypeFilter((value as string) || undefined);
                }}
                style={controlFontSize}
                selectOnBlur={false}
              />
            </GridColumn>

            <GridColumn mobile={16} tablet={6} computer={4} style={colStyle}>
              <Dropdown
                placeholder='User roles...'
                fluid
                search
                selection
                clearable
                multiple
                options={userRoleOptions}
                value={userRolesFilter}
                onChange={(_, { value }) => {
                  setCurrentPage(1);
                  setUserRolesFilter((value as string[]) || []);
                }}
                style={controlFontSize}
                selectOnBlur={false}
              />
            </GridColumn>
          </Grid>
        </div>
      </div>
      {error && <Message negative>{error}</Message>}
      {loading && <Loader active inline="centered" />}

      {!loading && !error && activities.length === 0 && (
        <Message info>No activity found matching your filters.</Message>
      )}

      {!loading && !error && activities.length > 0 && (
        <div className="card">
          <div className='admin-companies-search-meta' style={{ marginBottom: '0.5rem' }}>
            Showing {activities.length > 0 ? (currentPage - 1) * PAGE_SIZE + 1 : 0} -{' '}
            {Math.min(currentPage * PAGE_SIZE, (currentPage - 1) * PAGE_SIZE + activities.length)} of{' '}
            {total} activity records
            {hasFilters && (
              <>
                {' - '}
                <button
                  type='button'
                  className='admin-companies-clear-link'
                  onClick={clearAllFilters}
                >
                  Clear filters
                </button>
              </>
            )}
            {activeFilterDescription && (
              <>
                <br />
                {activeFilterDescription}
              </>
            )}
          </div>
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <STable striped>
              <STable.Header>
                <STable.Row>
                  <STable.HeaderCell
                    onClick={handleToggleSortByDate}
                    style={{ cursor: 'pointer' }}
                  >
                    Date{getSortIcon()}
                  </STable.HeaderCell>
                  <STable.HeaderCell>User</STable.HeaderCell>
                  <STable.HeaderCell>Role</STable.HeaderCell>
                  <STable.HeaderCell>Company</STable.HeaderCell>
                  <STable.HeaderCell>Project</STable.HeaderCell>
                  <STable.HeaderCell>Portfolio</STable.HeaderCell>
                  <STable.HeaderCell>Type</STable.HeaderCell>
                  {/* <STable.HeaderCell>Metadata</STable.HeaderCell> */}
                </STable.Row>
              </STable.Header>
              <STable.Body>
                {activities.map((activity) => (
                  <STable.Row key={activity.id}>
                    <STable.Cell style={{ whiteSpace: 'nowrap' }}>
                      {new Date(activity.createdAt).toLocaleString()}
                    </STable.Cell>
                    <STable.Cell>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span>

                          {activity.user?.name || '(unknown)'}
                          {activity.user?.email && (
                            <Button
                              as="a"
                              secondary
                              size="small"
                              style={{ padding: '2px', minWidth: 0, minHeight: 0, backgroundColor: 'transparent' }}
                              href={
                                '/dashboard' +
                                location.search +
                                (location.search.indexOf('?') < 0 ? '?' : '&') +
                                'spoof=' +
                                encodeURIComponent(activity.user.email)
                              }
                              target="_blank"
                            >
                              <Icon name="external" color="blue" style={{ marginLeft: '4px'}}/>
                            </Button>
                          )}
                          {activity.user?.email && (
                            <span style={{ color: '#888', display: 'inline-block', width: '100%' }}>
                              {activity.user.email}
                            </span>
                          )}
                        </span>

                      </span>
                    </STable.Cell>
                    <STable.Cell>
                      {activity.user?.role?.code
                        ? roleLabel[activity.user.role.code] ?? activity.user.role.code
                        : ''}
                    </STable.Cell>
                    <STable.Cell>
                      {activity.user?.investor?.company?.name ??
                        activity.user?.installer?.company?.name ??
                        activity.user?.company?.name ??
                        ''}
                    </STable.Cell>
                    <STable.Cell>
                      {activity.project?.id ? (
                        <Link
                          to={`/project/${activity.project.parentId ?? activity.project.id
                            }`}
                        >
                          {activity.project.name}
                        </Link>
                      ) : (
                        activity.project?.name ?? ''
                      )}
                    </STable.Cell>
                    <STable.Cell>
                      {activity.portfolio?.id ? (
                        <Link
                          to={`/portfolio/${activity.portfolio.parentId ?? activity.portfolio.id
                            }`}
                        >
                          {activity.portfolio.name}
                        </Link>
                      ) : activity.project?.portfolio?.id ? (
                        <Link
                          to={`/portfolio/${activity.project.portfolio.parentId ??
                            activity.project.portfolio.id
                            }`}
                        >
                          {activity.project.portfolio.name}
                        </Link>
                      ) : (
                        activity.portfolio?.name ??
                        activity.project?.portfolio?.name ??
                        ''
                      )}
                    </STable.Cell>
                    <STable.Cell>{activity.activityType}</STable.Cell>
                    {/* <STable.Cell>
                    {activity.metadata
                      ? JSON.stringify(activity.metadata).slice(0, 80) +
                        (JSON.stringify(activity.metadata).length > 80 ? '…' : '')
                      : ''}
                  </STable.Cell> */}
                  </STable.Row>
                ))}
              </STable.Body>
            </STable>
          </div>
          <div
            style={{
              marginTop: '2rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {totalPages > 1 && (
              <Pagination
                activePage={currentPage}
                onPageChange={(_, { activePage }) => setCurrentPage(activePage as number)}
                totalPages={totalPages}
                siblingRange={1}
                boundaryRange={1}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: '←', icon: false }}
                nextItem={{ content: '→', icon: false }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

